<template>
  <View gap="lg" direction="column" id="pd-escalations">
    <View v-if="!hideHeader" justify="space-between">
      <Text variant="h2" ref="escalationsSection">
        Intelligent Escalations
        <span v-if="hasEscalations">({{ escalationsAmount }})</span>
      </Text>

      <ScrollableHorizontallyControls :scrollable-container="escalationsScrollableContainerRef" />
    </View>

    <View direction="column" gap="md">
      <EmptyState
        v-if="!hasEscalations"
        icon="sparkle"
        color="violet"
        title="What are Intelligent Escalations?"
        description="For questions don't have the best answer for, we reach out for your input. We will then reply and enrich your profile to prevent this question in the future."
      />

      <ScrollableHorizontallyContainer ref="escalationsScrollableContainerRef">
        <View class="card-items" gap="md" padding-bottom="md" v-if="hasEscalations">
          <IntelligentEscalationCard
            class="card-item"
            v-for="question in escalations"
            :key="question.id"
            :question="question"
            @click="showModal(question)"
          />
        </View>
      </ScrollableHorizontallyContainer>
    </View>

    <IntelligentEscalationModal
      v-if="selectedEscalation"
      :escalation="selectedEscalation"
      @closed="resetSelectedEscalation"
      ref="intelligentEscalationModalRef"
    />
  </View>
</template>

<script setup lang="ts">
import { until } from '@vueuse/core';
import { computed, ref, nextTick, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import {
  EmptyState,
  ScrollableHorizontallyContainer,
  ScrollableHorizontallyControls,
  Text,
  View,
  useAlertsStore,
} from '@tallo/design-system';
import {
  IntelligentEscalationCard,
  IntelligentEscalationDto,
  IntelligentEscalationModal,
  usePropertyIntelligentEscalationsQuery,
} from '@tallo/investor/intelligent-escalations';

defineProps<{ hideHeader?: boolean }>();

const route = useRoute();
const router = useRouter();
const alertsStore = useAlertsStore();
const { data, status } = usePropertyIntelligentEscalationsQuery();
const escalationsScrollableContainerRef = ref();
const intelligentEscalationModalRef = ref<InstanceType<typeof IntelligentEscalationModal> | null>(null);
const selectedEscalation = ref<IntelligentEscalationDto>();

const escalations = computed(() => data.value || []);
const isLoaded = computed(() => status.value === 'success');
const escalationsAmount = computed(() => escalations.value?.length);
const hasEscalations = computed(() => escalationsAmount.value > 0);

onMounted(openQuestionBasedOnQueryParam);
// A link followed while this pane is already showing changes only the query, so nothing remounts.
watch(() => route.query.questionId, openQuestionBasedOnQueryParam);

function showModal(escalation: IntelligentEscalationDto) {
  selectedEscalation.value = escalation;

  nextTick(() => {
    intelligentEscalationModalRef.value?.open();
  });
}

function resetSelectedEscalation() {
  selectedEscalation.value = undefined;
}

async function openQuestionBasedOnQueryParam() {
  const escalationId = route.query.questionId;

  if (!escalationId) {
    return;
  }

  await until(isLoaded).toMatch((value) => !!value);

  // Consumed once read, so following the same link again opens it again.
  const query = { ...route.query };
  delete query.questionId;
  await router.replace({ query });

  const escalation = escalations.value.find(({ id }) => id === escalationId);

  if (escalation) {
    showModal(escalation);

    return;
  }

  // The list holds pending escalations only, so a link to one that was answered, dismissed or
  // expired finds nothing - say so, or the owner lands on a page that seems to have lost it.
  alertsStore.info('The escalation from your link is already closed: it was answered, dismissed, or it expired.');
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.card-items {
  .card-item {
    width: 28rem;
    flex-shrink: 0;
  }

  @include mobile {
    flex-direction: column;

    .card-item {
      width: 100%;
    }
  }
}

// With escalations present, the horizontal-scroll container is the first element in the panel.
// Its -2rem top "bleed" margin (added for card-shadow clearance during scroll) then extends up
// over the tabs and intercepts their clicks. Drop the TOP bleed only — the left/right/bottom
// bleed stays for the cards' shadows and hover lift.
:deep(.container-outer) {
  margin-top: 0;
}

:deep(.container-inner) {
  padding-top: 0;
}
</style>
